import React, { useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";
import { SearchOutlined } from "@ant-design/icons";
import { debounce } from "../utils/debounce";
import LearnerCourseService from "../services/LearnerCourseService";

const LearnerSearchBar = () => {
  const [searchValue, setSearchValue] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    setSearchValue("");
    setResults([]);
    setShowDropdown(false);
  }, [location.pathname]);

  const debouncedSearch = React.useCallback(
    debounce(async (value) => {
      if (value.trim()) {
        try {
          const { courses } = await LearnerCourseService.getLearnerCourses({
            search: value,
            limit: 5,
            page: 1,
          });
          setResults(courses);
          setShowDropdown(true);
        } catch (e) {
          setResults([]);
          setShowDropdown(false);
        }
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    }, 400),
    []
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSelect = (course) => {
    setShowDropdown(false);
    setSearchValue("");
    if (course.bought) {
      navigate(`/learner/my-courses/${course.slug}`);
    } else {
      navigate(`/courses/${course.slug}`);
    }
  };

  const handleShowMore = () => {
    // Lấy các filter hiện tại từ URL
    const params = new URLSearchParams(location.search);
    params.set("search", searchValue);
    // Giữ lại các filter khác nếu có
    navigate({ pathname: "/all-courses", search: params.toString() });
    setShowDropdown(false);
    setSearchValue("");
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        width: 400,
        borderRadius: 18,
        padding: 4,
      }}
      className="navbar-item navbar-search"
    >
      <Input
        value={searchValue}
        onChange={handleInputChange}
        placeholder="Search for anything..."
        size="middle"
        prefix={
          <SearchOutlined
            style={{ cursor: "pointer", color: '#1976d2', fontSize: 20, verticalAlign: 'middle', marginRight: 4 }}
            onClick={() => handleShowMore()}
          />
        }
        style={{
          borderRadius: 18,
          border: '1.2px solid #1976d2',
          paddingLeft: 14,
          paddingRight: 32,
          fontSize: 15,
          height: 36,
          background: '#fff',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)',
          transition: 'none',
        }}
        inputMode="search"
        onFocus={() => { if (results.length) setShowDropdown(true); }}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.stopPropagation();
            handleShowMore();
          }
        }}
        allowClear
      />
      {showDropdown && results.length > 0 && (
        <div
          className="card custom-dropdown"
          style={{
            position: "absolute",
            top: 42,
            left: 0,
            width: "100%",
            zIndex: 1000,
            borderRadius: 10,
            boxShadow: "0 2px 8px rgba(25, 118, 210, 0.07)",
            padding: '8px 0 4px 0',
            maxHeight: 'none',
            overflowY: 'visible',
          }}
        >
          <ul className="list-group list-group-flush" style={{ maxHeight: 'none', overflowY: 'visible', padding: 0 }}>
            {results.map(course => (
              <li
                key={course.slug}
                className="list-group-item"
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: '10px 18px',
                  border: 'none',
                  borderRadius: 6,
                  margin: '2px 8px',
                  transition: 'background 0.2s',
                }}
                onClick={() => handleSelect(course)}
                onMouseOver={e => e.currentTarget.style.background = '#f0f7ff'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                <img src={course.imageUrl} alt={course.name} style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6, flexShrink: 0, marginRight: 12 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{course.name}</div>
                  <div style={{ color: "#888", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{course.author}</div>
                </div>
              </li>
            ))}
            <li
              className="list-group-item text-center"
              style={{
                color: "#1976d2",
                fontWeight: 500,
                cursor: "pointer",
                padding: '10px 0 6px 0',
                background: 'transparent',
                border: 'none',
                borderRadius: 6,
                margin: '2px 8px 0 8px',
                transition: 'background 0.2s',
              }}
              onClick={handleShowMore}
              onMouseOver={e => e.currentTarget.style.background = '#e3f0ff'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            >
              Show more…
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default LearnerSearchBar; 